import { useMemo, useState } from 'react';
import { Loader2, ShieldAlert } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { useAdminModerationJobs, useModerationAction } from '../../hooks/useAdminModeration';
import type { AdminModerationJob, ModerationStatus } from '../../api/adminModeration';

const moderationStates = ['pending_review', 'flagged', 'approved', 'rejected'] as const;

export function ModerationQueue() {
  const [activeTab, setActiveTab] = useState<typeof moderationStates[number]>('pending_review');
  const [actioningId, setActioningId] = useState<string | null>(null);

  const { data, isLoading, isError, refetch } = useAdminModerationJobs(activeTab);
  const moderationAction = useModerationAction(activeTab);

  const jobs = data ?? [];

  return (
    <div className="min-h-screen bg-[#080a1a] pb-24">
      <div className="mx-auto max-w-5xl px-4 py-16 sm:px-6 lg:px-8">
        <h1 className="text-3xl font-semibold text-white sm:text-4xl">Moderation queue</h1>
        <p className="mt-2 text-sm text-gray-400">
          Review reported listings, run inclusive language checks, and keep the marketplace healthy.
        </p>

        <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as typeof activeTab)} className="mt-10 space-y-6">
          <TabsList className="flex gap-2 border border-white/10 bg-white/5 p-2">
            {moderationStates.map((state) => (
              <TabsTrigger
                key={state}
                value={state}
                className="text-sm capitalize text-gray-200 data-[state=active]:bg-gold-gradient data-[state=active]:text-black"
              >
                {state.replace('_', ' ')}
              </TabsTrigger>
            ))}
          </TabsList>

          <TabsContent value={activeTab} className="space-y-4">
            {isLoading && (
              <Card className="border-white/10 bg-white/[0.02]">
                <CardContent className="flex items-center gap-3 p-8 text-sm text-gray-300">
                  <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
                  Loading moderation queue…
                </CardContent>
              </Card>
            )}

            {isError && (
              <Card className="border-red-500/30 bg-red-500/10">
                <CardContent className="flex flex-col gap-3 p-8 text-sm text-red-100">
                  <div className="flex items-center gap-2">
                    <ShieldAlert className="h-5 w-5" />
                    Unable to load moderation queue
                  </div>
                  <Button variant="outline" className="w-fit border-white/30 text-white" onClick={() => refetch()}>
                    Retry
                  </Button>
                </CardContent>
              </Card>
            )}

            {!isLoading &&
              !isError &&
              jobs.map((job) => (
                <ModerationCard
                  key={job.id}
                  job={job}
                  actioningId={actioningId}
                  onAction={async (nextStatus, note) => {
                    setActioningId(String(job.id));
                    try {
                      await moderationAction.mutateAsync({ id: job.id, nextStatus, note });
                      toast.success(
                        nextStatus === 'approved'
                          ? 'Job approved'
                          : nextStatus === 'rejected'
                          ? 'Job removed'
                          : 'Action recorded',
                      );
                    } catch (error) {
                      console.error(error);
                      toast.error(error instanceof Error ? error.message : 'Unable to update listing.');
                    } finally {
                      setActioningId(null);
                    }
                  }}
                />
              ))}

            {!isLoading && !isError && jobs.length === 0 && (
              <Card className="border-white/10 bg-white/[0.02]">
                <CardContent className="p-12 text-center text-sm text-gray-300">
                  No jobs in this bucket. Grab tea ☕.
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

type ModerationCardProps = {
  job: AdminModerationJob;
  actioningId: string | null;
  onAction: (nextStatus: ModerationStatus, note?: string) => Promise<void>;
};

function ModerationCard({ job, actioningId, onAction }: ModerationCardProps) {
  const actionDisabled = actioningId === String(job.id);
  const submittedDate = useMemo(
    () => new Date(job.submittedAt).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' }),
    [job.submittedAt],
  );

  const actionButtons = [
    {
      label: 'Approve',
      variant: 'default' as const,
      onClick: () => onAction('approved'),
      visible: job.status !== 'approved',
    },
    {
      label: 'Request edits',
      variant: 'outline' as const,
      onClick: () => onAction('flagged', 'Requested edits via moderation queue'),
      visible: job.status !== 'flagged',
    },
    {
      label: 'Remove listing',
      variant: 'ghost' as const,
      onClick: () => onAction('rejected', 'Removed for guideline breach'),
      visible: job.status !== 'rejected',
    },
  ];

  return (
    <Card className="border-white/10 bg-white/[0.03]">
      <CardHeader className="flex flex-col gap-2 md:flex-row md:items-start md:justify-between">
        <div>
          <CardTitle className="text-xl text-white">{job.title}</CardTitle>
          <p className="text-sm text-gray-400">{job.employerName}</p>
          <p className="text-xs text-gray-500">Submitted {submittedDate}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Badge className="border-white/10 bg-white/[0.07] text-gray-200 capitalize">{job.status.replace('_', ' ')}</Badge>
          {job.reports > 0 && (
            <Badge className="border-red-500/40 bg-red-500/20 text-red-200">{job.reports} reports</Badge>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-4 p-6 pt-0">
        <div className="rounded-lg border border-white/10 bg-white/[0.05] p-4 text-sm text-gray-200">
          <p className="font-semibold text-white">Sanitised overview preview</p>
          <p className="mt-2 whitespace-pre-line text-gray-300">{job.description}</p>
        </div>
        <div className="flex flex-wrap gap-3">
          {actionButtons
            .filter((button) => button.visible)
            .map((button) => (
              <Button
                key={button.label}
                variant={button.variant}
                className={
                  button.variant === 'default'
                    ? 'bg-gold-gradient text-black hover:brightness-110'
                    : button.variant === 'outline'
                    ? 'border-white/20 text-white hover:bg-white/10'
                    : 'text-red-300 hover:text-red-200'
                }
                disabled={actionDisabled}
                onClick={button.onClick}
              >
                {actionDisabled ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                {button.label}
              </Button>
            ))}
        </div>
      </CardContent>
    </Card>
  );
}
